import type { Feature } from "geojson";
import { t } from "i18next";

import { AppMessageLevel, addMessage } from "@/features/app/appSlice";

import store from "@/app/store";
import {
  KeyShortcut,
  SpecialKeys,
} from "@/features/app/components/key_shortcut";
import { PopupMenu } from "@/features/app/components/modals/PopupMenu";
import { deepMergeKeepArrays } from "@/features/editor/components/overlay_layers/utils";
import addNestedCoordinate from "@/features/geojson_editor/libs/geojson/addNestedCoordinate";
import { getFeatureId } from "@/features/geojson_editor/libs/geojson/geometryUtils";
import removeNestedCoordinate from "@/features/geojson_editor/libs/geojson/removeNestedCoordinate";
import updateNestedCoordinate from "@/features/geojson_editor/libs/geojson/updateNestedCoordinate";
import { updateCoordinates } from "@/features/geojson_editor/libs/updateCoordinates";
import {
  createEditorMetaFromFeature,
  type MetaFeatureMidpoint,
} from "@/features/geojson_editor/meta";
import {
  BaseMode,
  type GeojsonEditorDelta,
} from "@/features/geojson_editor/modes/BaseMode";
import { isShiftPressed } from "@/features/map/hooks/events/selectors";
import type { EventData } from "@/features/map/hooks/events/types";

export class DirectSelectMode extends BaseMode {
  generateMetaFeatures(): Feature[] {
    if (!this.shouldRecalculateMetaFeatures()) {
      return this._cachedMetaFeatures;
    }

    this._cachedMetaFeatures = Object.values(this._features).flatMap((f) =>
      createEditorMetaFromFeature(f, {
        includeCenterPoint: true,
        includeFill: true,
        includeLine: true,
        includeVertex: true,
      }),
    );

    return this._cachedMetaFeatures;
  }

  /**
   * Apply a delta with updates to the geojson features for the current mode.
   */
  applyDelta(delta: GeojsonEditorDelta) {
    const updatedFeatures = { ...this._features };
    Object.entries(delta.updated ?? {}).forEach(([id, feature]) => {
      updatedFeatures[id] = deepMergeKeepArrays(this._features[id], feature);
    });
    delta.added?.forEach((feature) => {
      updatedFeatures[feature.id] = feature;
    });
    delta.deleted?.forEach((id) => {
      delete updatedFeatures[id];
    });
    this._features = updatedFeatures;
    this.forceReRender();
  }

  // Only one feature can be selected at a time
  setFeatures(fs: Record<string, Feature>): void {
    const feature = Object.values(fs).at(0);
    if (!feature) {
      super.setFeatures({});
      return;
    }

    const featureId = getFeatureId(feature);
    super.setFeatures({
      [featureId]: feature,
    });
  }

  __removeVertex(e: EventData) {
    const { features } = e;
    const metaFeature = features.at(0);
    const feature = this._features[metaFeature.properties.metaFeatureId];
    if (!feature) return;
    const updatedFeature = removeNestedCoordinate(
      feature,
      metaFeature.properties.metaCoordinateIdx,
    );

    this.onChange(
      {
        updated: {
          [feature.id]: updatedFeature,
        },
      },
      true,
    );
    return true;
  }

  __deleteFeature() {
    const feature = Object.values(this._features).at(0);
    if (!feature) return;
    this.onChange(
      {
        deleted: [feature.id],
      },
      true,
    );
  }

  eventHandlers: BaseMode["eventHandlers"] = {
    onDelete: () => {
      this.__deleteFeature();
    },
    onFillDrag: (e) => {
      const { features, displacement } = e;
      const metaFeature = features.at(0);
      const feature = this._features[metaFeature.properties.metaFeatureId];
      if (!feature) return;
      if (feature.geometry.type === "GeometryCollection") return;
      // Move all coordinates by the displacement
      const updated = updateCoordinates(feature, (coord) => {
        if (coord.length === 2) {
          return [coord[0] + displacement.lng, coord[1] + displacement.lat];
        } else if (coord.length === 3) {
          return [
            coord[0] + displacement.lng,
            coord[1] + displacement.lat,
            coord[2],
          ];
        }
        // fallback, should not happen
        return coord;
      });
      this.onChange(
        {
          updated: {
            [feature.id]: updated,
          },
        },
        true,
      );
      return true;
    },
    onFillRightClick: (e) => {
      this._openPopupMenu(
        <>
          <PopupMenu.Item
            description={<KeyShortcut hotkeys={[SpecialKeys.DELETE]} />}
            text={t("actions.delete", { ns: "editor" })}
            onClick={() => this.__deleteFeature()}
          />
        </>,
        { x: e.originalEvent.clientX, y: e.originalEvent.clientY },
      );
    },
    onMidPointClick: (e) => {
      const { features } = e;
      const metaFeature = features.at(0) as unknown as MetaFeatureMidpoint;
      const feature = this._features[metaFeature.properties.metaFeatureId];
      if (!feature) return;

      try {
        const updatedFeature = addNestedCoordinate(
          feature,
          metaFeature.properties.metaCoordinateIdx,
          metaFeature.geometry.coordinates,
        );

        this.onChange(
          {
            updated: {
              [feature.id]: updatedFeature,
            },
          },
          true,
        );
        return true;
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Could not add coordinate";

        if (!message) {
          console.error(`Error adding coordinate: ${error}`);
        }

        store.dispatch(
          addMessage({
            id: "add-coordinate-error",
            level: AppMessageLevel.ERROR,
            message,
          }),
        );
        return false;
      }
    },
    onVertexClick: (e) => {
      if (isShiftPressed(e)) {
        return this.__removeVertex(e);
      }
      // Always prevent default
      return true;
    },
    onVertexDrag: (e) => {
      const { features, displacement } = e;
      const metaFeature = features.at(0);
      const feature = this._features[metaFeature.properties.metaFeatureId];
      if (!feature) return;
      if (feature.geometry.type === "GeometryCollection") return;
      // Move only the dragged vertex
      const updatedFeature = updateNestedCoordinate(
        feature,
        metaFeature.properties.metaCoordinateIdx,
        [displacement.lng, displacement.lat],
        true,
      );
      this.onChange(
        {
          updated: {
            [feature.id]: updatedFeature,
          },
        },
        true,
      );
      return true;
    },
    onVertexRightClick: (e) => {
      try {
        return this.__removeVertex(e);
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Could not remove vertex";

        if (!message) {
          console.error(`Error removing vertex: ${error}`);
        }

        store.dispatch(
          addMessage({
            id: "remove-vertex-error",
            level: AppMessageLevel.ERROR,
            message,
          }),
        );
        return true;
      }
    },
  };
}
